import { useMemo } from "react";

import { tooltipFormatter } from "@/components/Elements/Charts/helpers";
import {
  CHART_HISTOGRAM_DEFAULT_COLOR,
  CHART_HISTOGRAM_SELECTED_COLOR,
} from "@/constants/Charts";

export const useHistogramChartOptions = ({
  data,
  categoriesTitle,
  valuesTitle,
  formatCategoryLabel,
  formatValueLabel,
  formatTooltipValue,
  controlled,
}) => {
  const categories = useMemo(
    () => data?.map(({ category }) => category),
    [data]
  );

  const formattedCategories = useMemo(() => {
    if (!controlled?.selectedCategory) return categories;

    return categories?.map((category) =>
      category !== controlled.selectedCategory
        ? { value: category, textStyle: { opacity: 0.5 } }
        : category
    );
  }, [categories, controlled?.selectedCategory]);

  return useMemo(
    () => ({
      color: CHART_HISTOGRAM_DEFAULT_COLOR,
      series: {
        type: "bar",
        data: data?.map(({ value, category }) => ({
          value,
          selected: category === controlled?.selectedCategory,
        })),
        selectedMode: "single",
        select: {
          itemStyle: {
            borderColor: "transparent",
            color: CHART_HISTOGRAM_SELECTED_COLOR,
          },
        },
      },
      grid: {
        top: "20px",
        right: "20px",
        bottom: categoriesTitle ? "20px" : "10px",
        left: valuesTitle ? "35px" : "15px",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: formattedCategories,
        name: categoriesTitle,
        nameLocation: "middle",
        nameGap: 25,
        triggerEvent: true, // make categories clickable to be able to filter even if value = 0
        axisLabel: {
          formatter: formatCategoryLabel,
        },
      },
      yAxis: {
        type: "value",
        name: valuesTitle,
        nameLocation: "middle",
        nameRotate: 90,
        nameGap: 55,
        axisLabel: {
          formatter: formatValueLabel,
        },
      },
      tooltip: {
        trigger: "axis", // trigger tooltip on axis to make sure to display tooltip even is value = 0
        appendToBody: true,
        axisPointer: {
          type: "shadow",
          shadowStyle: {
            color: CHART_HISTOGRAM_DEFAULT_COLOR,
            opacity: 0.1,
          },
        },
        formatter: ([{ name, value }]) =>
          tooltipFormatter({
            name: formatCategoryLabel(name),
            value: formatTooltipValue(value),
          }),
      },
    }),
    [
      data,
      formattedCategories,
      categoriesTitle,
      formatCategoryLabel,
      valuesTitle,
      formatValueLabel,
      controlled?.selectedCategory,
    ]
  );
};
